import React from 'react';
import { motion } from 'framer-motion';

// Nespresso brand colors
const NESPRESSO = {
  brown: '#986f38',
  brownHover: '#815e2f',
  gold: '#c69f36',
  black: '#000000',
  darkGray: '#292929',
};

interface PlaceBagInstructionProps {
  /** Optional callback when the instruction is acknowledged */
  onAcknowledge?: () => void;
  /** Whether to show the acknowledge button */
  showAcknowledgeButton?: boolean;
  /** Custom instruction text */
  instructionText?: string;
}

const PlaceBagInstruction: React.FC<PlaceBagInstructionProps> = ({
  onAcknowledge,
  showAcknowledgeButton = false,
  instructionText = 'Place your first bag on the tray',
}) => {
  return (
    <motion.div
      className="flex flex-col items-center justify-center p-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Visual indicator - tray icon with animated arrow */}
      <div className="relative mb-8">
        {/* Tray illustration */}
        <motion.div
          className="relative w-48 h-32 rounded-lg flex items-center justify-center"
          style={{
            backgroundColor: `${NESPRESSO.gold}15`,
            border: `2px dashed ${NESPRESSO.gold}40`,
          }}
          animate={{
            borderColor: [`${NESPRESSO.gold}40`, `${NESPRESSO.gold}80`, `${NESPRESSO.gold}40`],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          {/* Tray surface pattern */}
          <div className="absolute inset-4 rounded-md" style={{ backgroundColor: `${NESPRESSO.gold}10` }}>
            <div className="w-full h-full flex items-center justify-center">
              {/* Bag icon */}
              <svg
                className="w-16 h-16"
                fill="none"
                stroke={NESPRESSO.gold}
                strokeWidth={1.5}
                viewBox="0 0 24 24"
                style={{ opacity: 0.6 }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                />
              </svg>
            </div>
          </div>

          {/* "TRAY" label */}
          <span
            className="absolute bottom-2 text-xs uppercase tracking-widest font-light"
            style={{ color: NESPRESSO.gold }}
          >
            Tray
          </span>
        </motion.div>

        {/* Animated arrow pointing down to tray */}
        <motion.div
          className="absolute -top-12 left-1/2 -translate-x-1/2 flex flex-col items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <motion.svg
            className="w-8 h-8"
            fill="none"
            stroke={NESPRESSO.gold}
            strokeWidth={2}
            viewBox="0 0 24 24"
            animate={{ y: [0, 8, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </motion.svg>
        </motion.div>

        {/* Glowing ring effect */}
        <motion.div
          className="absolute inset-0 rounded-lg pointer-events-none"
          style={{
            boxShadow: `0 0 30px ${NESPRESSO.gold}30`,
          }}
          animate={{
            boxShadow: [
              `0 0 20px ${NESPRESSO.gold}20`,
              `0 0 40px ${NESPRESSO.gold}40`,
              `0 0 20px ${NESPRESSO.gold}20`,
            ],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      {/* Instruction text */}
      <motion.h2
        className="text-2xl font-light text-white text-center mb-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        {instructionText}
      </motion.h2>

      {/* Supporting text */}
      <motion.p
        className="text-white/60 text-center font-light max-w-xs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        Position the bag of capsules in the center of the scanning tray
      </motion.p>

      {/* Optional acknowledge button */}
      {showAcknowledgeButton && onAcknowledge && (
        <motion.button
          onClick={onAcknowledge}
          className="mt-8 px-8 py-3 text-white font-medium uppercase tracking-wider transition-colors"
          style={{
            backgroundColor: NESPRESSO.brown,
            borderRadius: '3px',
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          whileHover={{ backgroundColor: NESPRESSO.brownHover }}
          whileTap={{ scale: 0.98 }}
        >
          I've Placed My Bag
        </motion.button>
      )}
    </motion.div>
  );
};

export default PlaceBagInstruction;
